import React, { useEffect, useRef, useState } from 'react';

/**
 * Branded full-app loading indicator — the signup-modal visual identity
 * (masked conic cyan→indigo ring + soft glow halos) reused everywhere a
 * loading screen appears, so the boot sequence never jumps between two
 * different spinner styles.
 *
 * The optional message line is either a static string or a carousel that
 * crossfades between messages (same 2s visible / 0.5s fade cadence the old
 * LoadingSequence used). All motion is reduced-motion-safe.
 *
 * `fullscreen` owns its own background (#050505) and min-height (min-h-dvh),
 * so it centers correctly no matter what wrapper a route gives it — including
 * wrappers that only set min-height, where h-full collapses to content height.
 */

type LoaderSize = 'sm' | 'md' | 'lg';

/** Ring diameters + the blurred halo inset per size. */
const RING_SIZES: Record<LoaderSize, { box: string; halo: string }> = {
  sm: { box: 'h-6 w-6', halo: 'inset-[-6px]' },
  md: { box: 'h-11 w-11', halo: 'inset-[-10px]' },
  lg: { box: 'h-16 w-16', halo: 'inset-[-14px]' },
};

export interface BrainiacsLoaderProps {
  /**
   * fullscreen — fixed overlay filling the viewport (auth boot, route loads).
   * section    — centered block with page-level spacing (Suspense fallbacks).
   * inline     — single row (modal spinners, list footers).
   */
  variant?: 'fullscreen' | 'section' | 'inline';
  /** Ring size. Defaults per variant: fullscreen=lg, section=lg, inline=sm. */
  size?: LoaderSize;
  /** Static message. Takes precedence over `messages`. */
  message?: string;
  /** Cycling messages with a 0.5s crossfade. */
  messages?: readonly string[];
  /** Smaller muted line under the message. */
  subtitle?: string;
  /**
   * Cyan/indigo "smoke" bleeding in from the screen edges. Fullscreen only;
   * defaults to true for fullscreen and is ignored otherwise.
   */
  edgeGlow?: boolean;
  className?: string;
}

const BrainiacsLoader: React.FC<BrainiacsLoaderProps> = ({
  variant = 'inline',
  size,
  message,
  messages,
  subtitle,
  edgeGlow,
  className = '',
}) => {
  const resolvedSize = size ?? (variant === 'inline' ? 'sm' : 'lg');
  const ring = RING_SIZES[resolvedSize];

  // ── Message carousel ───────────────────────────────────────────────────
  const list = message ? [message] : messages ?? [];
  // Start at a random message so repeat visits don't always open on the same
  // line — with long message lists the loading screen feels fresh each time.
  const [msgIndex, setMsgIndex] = useState(() =>
    list.length > 1 ? Math.floor(Math.random() * list.length) : 0,
  );
  const [visible, setVisible] = useState(true);
  // Guards against a `messages` array shrinking below the current index.
  const safeIndex = Math.min(msgIndex, Math.max(list.length - 1, 0));

  const timerRef = useRef<number | undefined>(undefined);
  const swapRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (list.length < 2) return undefined;
    const showDuration = 2000; // 2s visible
    const transitionDuration = 500; // 0.5s crossfade
    timerRef.current = window.setTimeout(() => {
      setVisible(false);
      swapRef.current = window.setTimeout(() => {
        setMsgIndex((prev) => (prev + 1) % list.length);
        setVisible(true);
      }, transitionDuration);
    }, showDuration);
    return () => {
      window.clearTimeout(timerRef.current);
      window.clearTimeout(swapRef.current);
    };
  }, [safeIndex, list.length]);

  const hasText = list.length > 0;
  const showDots = list.length > 1; // progress dots only make sense mid-carousel

  const textBlock = (hasText || subtitle) && (
    <div className="text-center">
      {hasText && (
        <p
          className="text-sm font-medium text-white/80"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(-6px)',
            transition: 'opacity 0.5s ease-out, transform 0.5s ease-out',
          }}
        >
          {list[safeIndex]}
        </p>
      )}
      {subtitle && <p className="mt-1 text-xs text-white/40">{subtitle}</p>}
      {showDots && (
        <div className="mt-4 flex items-center justify-center gap-1.5" aria-hidden="true">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="h-1.5 w-1.5 animate-pulse rounded-full bg-cyan-300/80 motion-reduce:animate-none"
              style={{ animationDelay: `${i * 240}ms` }}
            />
          ))}
        </div>
      )}
    </div>
  );

  // The spinning ring — masked conic gradient (signup modal identity).
  const ringBlock = (
    <div className={`relative ${ring.box}`}>
      <div
        className="absolute inset-0 animate-spin rounded-full motion-reduce:animate-none"
        style={{
          background:
            'conic-gradient(from 0deg, transparent 0%, rgba(125,211,252,0.9) 35%, rgba(82,96,255,0.95) 70%, transparent 100%)',
          maskImage: 'radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 2px))',
          WebkitMaskImage: 'radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 2px))',
        }}
      />
      {/* Soft indigo halo behind the ring — the "spinning aura" of the modal. */}
      <div
        className={`absolute ${ring.halo} rounded-full bg-[conic-gradient(from_180deg,transparent_0%,rgba(82,96,255,0.25)_100%)] blur-md motion-reduce:hidden`}
        aria-hidden="true"
      />
    </div>
  );

  const statusLabel = hasText ? list[safeIndex] : 'Loading';

  // ── Edge-glow ambience (fullscreen only) ───────────────────────────────
  // Two slow-drifting blurred blobs + an edge vignette so the cyan/indigo
  // smoke reads as bleeding in from the screen borders.
  const glowEnabled = variant === 'fullscreen' && (edgeGlow ?? true);
  const edgeGlowBlock = glowEnabled && (
    <div className="pointer-events-none absolute inset-0 overflow-hidden motion-reduce:hidden" aria-hidden="true">
      {/* Cyan smoke — left third */}
      <div
        className="animate-edge-glow-a absolute -left-24 top-1/4 h-[26rem] w-[26rem] rounded-full bg-cyan-300/10 blur-3xl"
      />
      {/* Indigo smoke — right third */}
      <div
        className="animate-edge-glow-b absolute -right-24 bottom-1/4 h-[30rem] w-[30rem] rounded-full bg-[#5260FF]/12 blur-3xl"
      />
      {/* Edge vignette — tints the screen border itself */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse at center, transparent 55%, rgba(82,96,255,0.10) 78%, rgba(125,211,252,0.08) 100%)',
        }}
      />
    </div>
  );

  if (variant === 'inline') {
    return (
      <div role="status" aria-live="polite" className={`inline-flex items-center gap-3 ${className}`}>
        {ringBlock}
        {hasText && <span className="text-sm text-white/60">{list[safeIndex]}</span>}
        {!hasText && <span className="sr-only">Loading</span>}
      </div>
    );
  }

  if (variant === 'section') {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-label={statusLabel}
        className={`flex flex-col items-center justify-center gap-6 py-24 ${className}`}
      >
        {ringBlock}
        {textBlock}
      </div>
    );
  }

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={statusLabel}
      className={`relative flex h-full min-h-dvh w-full items-center justify-center bg-[#050505] px-6 ${className}`}
    >
      {edgeGlowBlock}
      <div className="relative flex flex-col items-center gap-6">
        {ringBlock}
        {textBlock}
      </div>
    </div>
  );
};

export default BrainiacsLoader;
